import React, { use, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import LoadingIndicator from 'shared/components/loading-indicator/loading-indicator';
import PageHeader from 'shared/components/page-header/page-header';
import PopUp from 'shared/components/pop-up/pop-up';
import { ColumnDefinition } from 'shared/components/table/columnDefinition';
import { TableOverflowMenuItem } from 'shared/components/table/hooks';
import Table from 'shared/components/table/table';
import RowItem from 'shared/components/table/types/row-item';
import { NestedKeyof } from 'shared/components/table/utils/nested-keyof-utils';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';
import Permission from 'shared/features/auth/authorization/enums/permission';
import usePermissionAuthorization from 'shared/features/auth/authorization/hooks/permission-authorization';
import ObjectSort from 'shared/models/object-sort';

import Role from 'features/role/domain/models/role';
import { useDeleteRole, useGetRoles } from 'features/role/domain/queries/role-queries';
import RoleDetailsPopup from 'features/role/views/role-details/role-details-popup.component';

import 'features/role/views/roles.scss';

import FilterSessionStorageKeys from 'shared/constants/filterSessionStorageKeys';

const Roles = (): React.JSX.Element => {
  const { t } = useTranslation('role');
  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const { hasPermission } = usePermissionAuthorization();
  const { mutate: deleteRole, isSuccess: deleteRoleIsSuccess, error: deleteRoleError } = useDeleteRole();

  const sessionStorageSortingKey = FilterSessionStorageKeys.ROLES_SORTING;

  const [sortFromSessionStorage, setSortFromSessionStorage] = useSessionStorage<ObjectSort<Role>>(sessionStorageSortingKey, {
    property: 'name',
    isAscending: true,
  });

  const columns: ColumnDefinition<Role, NestedKeyof<Role>>[] = [
    { key: 'name', label: t('nameHeaderLabel') },
    {
      key: 'roleType',
      label: t('roleTypeHeaderLabel'),
      renderCustomContentProvider(role): React.JSX.Element {
        return <>{t(`roleType.${role.roleType}`)}</>;
      },
    },
    { key: 'idpId', label: t('idpIdHeaderLabel') },
  ];

  const { data, refetch: refetchGetRoles, error, isLoading, isSuccess } = useGetRoles();

  const [currentRole, setCurrentRole] = useState<Role>();
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [detailsViewingMode, setDetailsViewingMode] = useState<ViewingMode>('none');
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false);
  const [deleteConfirmationAction, setDeleteConfirmationAction] = useState(() => (): void | Promise<void> => {});

  const openDetails = (viewingMode: ViewingMode, role: Role | undefined = undefined): void => {
    setCurrentRole(role);
    setIsDetailsOpen(true);
    setDetailsViewingMode(viewingMode);
  };

  const onRoleSaved = (role: Role): void => {
    setCurrentRole(role);
    refetchGetRoles();
  };

  const closeDetails = (): void => {
    setIsDetailsOpen(false);
    setCurrentRole(undefined);
  };

  useEffect(() => {
    if (error) {
      setErrorMessage(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (deleteRoleIsSuccess) {
      refetchGetRoles();
    } else if (deleteRoleError) {
      setErrorMessage(deleteRoleError ?? undefined);
    }
  }, [deleteRoleError, deleteRoleIsSuccess]);

  const handleDeleteRole = (role: Role): void => {
    setIsDeleteConfirmationOpen(true);
    setDeleteConfirmationAction(() => (): Promise<void> => confirmDeleteRole(role));
  };

  const confirmDeleteRole = async (role: Role): Promise<void> => {
    deleteRole(role.idpId);

    setDeleteConfirmationAction(() => (): void => {});
    setIsDeleteConfirmationOpen(false);
    setIsDetailsOpen(false);
  };

  const getOverflowMenuItems = (): Array<TableOverflowMenuItem<Role>> | undefined => {
    const menuItems = new Array<TableOverflowMenuItem<Role>>();
    if (hasPermission(Permission.ReadRole)) {
      menuItems.push({
        label: t('overflowMenu.view'),
        action: (role) => openDetails('viewing', role),
      });
    }
    if (hasPermission(Permission.UpdateRole)) {
      menuItems.push({
        label: t('overflowMenu.edit'),
        action: (role) => openDetails('editing', role),
      });
    }
    if (hasPermission(Permission.DeleteRole)) {
      menuItems.push({ label: 'divider' });
      menuItems.push({
        label: t('overflowMenu.delete'),
        action: (role) => handleDeleteRole(role),
      });
    }

    if (menuItems.length === 0) {
      return;
    } else if (menuItems.length === 2) {
      menuItems.splice(1, 0, { label: 'divider' });
    }

    return menuItems;
  };

  return (
    <div className="roles-container" data-testid="rolesContainer">
      <PageHeader
        title={t('pageTitle')}
        addBtnVisible={isSuccess && hasPermission(Permission.CreateRole)}
        onAdd={() => openDetails('creation')}
      />
      {isLoading && <LoadingIndicator />}
      {isSuccess && data && (
        <Table
          className="roles-container"
          data={data.map<RowItem<Role>>((role) => ({ data: role }))}
          columns={columns}
          onItemClick={(item: Role) => openDetails('viewing', item)}
          overflowMenuOptions={getOverflowMenuItems()}
          rowIdentifier={(item: Role) => item.idpId}
          onSortChanged={(property: NestedKeyof<Role>, isAscending: boolean) => setSortFromSessionStorage({ property, isAscending })}
          initialOrderBy={sortFromSessionStorage.property}
          initialOrderDirection={sortFromSessionStorage.isAscending ? 'asc' : 'desc'}
          useBuiltInSorter
        />
      )}
      {isDetailsOpen && (
        <RoleDetailsPopup
          selectedRole={currentRole}
          allRoles={data}
          defaultViewingMode={detailsViewingMode}
          isOpen={isDetailsOpen}
          onClose={closeDetails}
          onSaved={onRoleSaved}
          onDelete={() => currentRole && handleDeleteRole(currentRole)}
        />
      )}
      <PopUp
        isOpen={isDeleteConfirmationOpen}
        title={t('deleteRoleConfirmation.title', {
          roleName: currentRole?.name,
        })}
        body={t('deleteRoleConfirmation.bodyText')}
        primaryButtonText={t('deleteRoleConfirmation.confirmButton')}
        secondaryButtonText={t('deleteRoleConfirmation.cancelButton')}
        handleOnClose={() => setIsDeleteConfirmationOpen(false)}
        secondaryButtonAction={() => setIsDeleteConfirmationOpen(false)}
        primaryButtonAction={() => deleteConfirmationAction()}
      />
    </div>
  );
};

export default Roles;
